import { TStoryFrame } from 'context/StudioEditorProvider';
import { TLocalizedContent, TStoryLocalizedItemsWire } from 'types/storyWire';

export type TGroupWireStatus = 'DRAFT' | 'DISABLED' | 'LIVE' | 'SCHEDULED' | 'ENDED';

export const isPublishedStatus = (status: TGroupWireStatus): boolean =>
  status === 'LIVE' || status === 'SCHEDULED' || status === 'ENDED';

export type TPlacement = 'HOME';

/** The group title uses the same locale-map shape as element copy. */
export type TLocalizedText = TLocalizedContent;

/**
 * An editor frame plus the revision it last shipped at. `toWireItems` and `itemContent` both take
 * this: `v` lives on the wire item, not on the frame the editor edits.
 *
 * The old `TLoose*` / `TCopyValue` types are gone — copy is a plain string on both sides now, so
 * there is nothing left to widen.
 */
export type TStoryVersionedFrame = TStoryFrame & { v?: number };

/** The normalised, editor-shaped item. Everything downstream of `fromWireItems` uses this. */
export type TStoryItem = TStoryFrame & {
  v: number;
};

export type TStorySnapshot = {
  id: string;
  contentVersion: number;
  version: number;
  title: TLocalizedText;
  startsAt: string | null;
  endsAt: string | null;
  segmentId: string | null;
  pinned: boolean;
  coverMediaUrl: TLocalizedText;
  items: TStoryItem[];
  publishedAt: string | null;
};

export type TGroupDetail = {
  id: string;
  name: string;
  status: TGroupWireStatus;
  placement: TPlacement;
  displayOrder: number;
  liveSnapshot: TStorySnapshot | null;
  draftSnapshot: TStorySnapshot;
};

export type TSnapshotField = 'title' | 'cover' | 'schedule' | 'audience' | 'pinned' | 'items';

export type TSlideDiff = {
  added: number;
  removed: number;
  changed: number;
  reordered: boolean;
};

export type TSnapshotDiff = {
  fields: TSnapshotField[];
  slides: TSlideDiff;
};

export type TSnapshotMeta = {
  title: string;
  coverMediaUrl: TLocalizedText;
  slideCount: number;
  segmentId: string | null;
  startsAt: string | null;
  endsAt: string | null;
  pinned: boolean;
  publishedAt: string | null;
};

export type TGroupSummary = {
  id: string;
  name: string;
  status: TGroupWireStatus;
  placement: TPlacement;
  displayOrder: number;
  title: string;
  /** Resolved to `en` for the card thumbnail; the full map lives on the snapshot. */
  coverMediaUrl: string | null;
  slideCount: number;
  pinned: boolean;
  hasLive: boolean;
  segmentId: string | null;
  startsAt: string | null;
  endsAt: string | null;
  publishedAt: string | null;
  liveMeta: TSnapshotMeta | null;
  pending: TSnapshotDiff | null;
};

export type TCreateGroupBody = {
  name: string;
  placement: TPlacement;
};

export type TUpdateGroupBody = {
  name: string;
  placement: TPlacement;
  version: number;
  title: TLocalizedText;
  startsAt: string | null;
  endsAt: string | null;
  segmentId: string | null;
  pinned: boolean;
  coverMediaUrl: TLocalizedText;
  /** Wire-shaped: copy folded into locale maps, cover nested, per-variant fields only. Narrowed
   *  on purpose, so the compiler enumerates any save path that forgets to project. */
  items: TStoryLocalizedItemsWire;
};
